import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumb from "../components/Breadcrumb";
import { ChevronRight, ArrowRight } from "lucide-react";

/**
 * Generic content page used by many static routes.
 *
 * Props:
 *  - title, subtitle, intro, breadcrumb, children, aside
 *  - sections: Array<{ heading?, body?: string | string[] }>
 *  - linkSections: Array<{ heading?, items: Array<{ label, to, description? }> }>
 */
export default function ContentPage({
  title,
  subtitle,
  intro,
  sections = [],
  linkSections = [],
  breadcrumb,
  children,
  aside,
}) {
  // Sensible default breadcrumb if none provided
  const trail = breadcrumb || [{ label: "Home", to: "/" }, { label: title }];
  return (
    <div className="min-h-screen bg-[#f5f7f8] font-[Inter,system-ui,-apple-system,sans-serif] flex flex-col">
      <Header />
      <Breadcrumb trail={trail} />

      <section className="bg-gradient-to-b from-[#007a8a] to-[#005f6b] text-white">
        <div className="max-w-[1200px] mx-auto px-4 py-7 md:py-14">
          <h1 className="text-2xl md:text-4xl font-bold leading-tight">{title}</h1>
          {subtitle && <p className="mt-2 text-white/85 text-sm md:text-lg max-w-3xl">{subtitle}</p>}
        </div>
      </section>

      <main className="flex-1">
        <div className="max-w-[1200px] mx-auto px-4 py-6 md:py-8">
          <div className={`grid grid-cols-1 ${aside ? "lg:grid-cols-[1fr_320px]" : ""} gap-6`}>
            <article className="bg-white border border-gray-200 rounded-lg p-5 md:p-8">
              {intro && (
                <p className="text-[15px] text-gray-700 leading-relaxed mb-6">{intro}</p>
              )}

              {sections.map((s, i) => (
                <section key={`s-${i}`} className="mb-6 last:mb-0">
                  {s.heading && (
                    <h2 className="text-xl font-bold text-gray-900 mb-2">{s.heading}</h2>
                  )}
                  {Array.isArray(s.body) ? (
                    <ul className="space-y-2">
                      {s.body.map((b, j) => (
                        <li key={j} className="text-[14px] text-gray-700 leading-relaxed flex gap-2">
                          <span className="text-[#007a8a] mt-2 w-1.5 h-1.5 rounded-full bg-[#007a8a] shrink-0"/>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[14px] text-gray-700 leading-relaxed whitespace-pre-line">{s.body}</p>
                  )}
                </section>
              ))}

              {linkSections.map((ls, i) => (
                <section key={`ls-${i}`} className="mb-6 last:mb-0">
                  {ls.heading && (
                    <h2 className="text-xl font-bold text-gray-900 mb-3">{ls.heading}</h2>
                  )}
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {ls.items.map((it) => (
                      <li key={it.to}>
                        <Link
                          to={it.to}
                          className="group flex items-start gap-3 p-3 rounded-md border border-gray-200 hover:border-[#007a8a] hover:bg-[#f5fbfc] transition-colors"
                        >
                          <div className="w-8 h-8 rounded-md bg-[#eef6f7] text-[#007a8a] flex items-center justify-center shrink-0">
                            <ArrowRight size={15}/>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[14px] font-semibold text-gray-900 group-hover:text-[#007a8a]">
                              {it.label}
                            </div>
                            {it.description && (
                              <div className="text-xs text-gray-500 mt-0.5">{it.description}</div>
                            )}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}

              {children}

              <hr className="my-6 border-gray-100"/>
              <div className="text-xs text-gray-500 flex items-center gap-1">
                Last updated 28 May 2026
                <span className="ml-auto">
                  <Link to="/" className="text-[#007a8a] hover:underline inline-flex items-center gap-1">
                    Back to companies <ChevronRight size={12}/>
                  </Link>
                </span>
              </div>
            </article>

            {aside && (
              <aside className="space-y-4">{aside}</aside>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
